import { AutoThrow } from "@pdfking/errors"
import { UInt8Collect } from "@pdfking/io"
import type { MergePage, PdfiumAPI } from "@pdfking/pdfium"
import { Provision } from "@pdfking/pdfium"
import type { PDFMergeFunction, PDFMergeInput } from "@pdfking/renderer-api"
import { Base64Encode, JSONEncode, StringToUInt8 } from "@pdfking/tfunc"
import type { AsyncSequence } from "@pdfking/tfunc"

import { INTEGRITY_LENGTH } from "../internal/StreamFormat"

// Rust writes the whole merge (see StreamFormat) and pulls its pages one per
// step: each page is read in before the step that takes it, so only one is
// ever held, and its bytes are handed on as soon as they are written.
async function* MergeSections(
  api: PdfiumAPI,
  pages: AsyncSequence<PDFMergeInput>
): AsyncGenerator<Uint8Array> {
  let staged: MergePage | undefined
  const input = (function* () {
    while (staged) {
      const page = staged
      staged = undefined
      yield page
    }
  })()
  // the integrity line goes in front once the merge is over (see
  // PDFMergeIntegrity): every offset written is counted after it
  const output = await api.merge(input, BigInt(INTEGRITY_LENGTH))

  for await (const { stream, size, meta } of pages) {
    staged = {
      bytes: await UInt8Collect(stream, size),
      meta: Base64Encode(StringToUInt8(JSONEncode(meta))),
    }
    yield AutoThrow(output.next().value!)
  }
  for (const tail of output) yield AutoThrow(tail)
}

export const PDFMerge: PDFMergeFunction = pages => Provision(api => MergeSections(api, pages))
